<script>
  import { onMount, onDestroy } from 'svelte';
  import { fade, fly } from 'svelte/transition';
  import { DASHBOARD_CONFIG } from '../../config.js';

  const statusTints = {
    NOT_READY: 'from-red-100 to-red-50 border-red-200',
    PACKING: 'from-yellow-100 to-yellow-50 border-yellow-200',
    PARTIALLY_READY: 'from-orange-100 to-orange-50 border-orange-200',
    READY: 'from-teal-100 to-teal-50 border-teal-200',
    ON_ROUTE: 'from-blue-100 to-blue-50 border-blue-200',
    DELIVERED: 'from-emerald-100 to-emerald-50 border-emerald-200',
    CANCELLED: 'from-gray-100 to-gray-50 border-gray-200',
    RETURNED: 'from-red-100 to-red-50 border-red-200',
  };

  const messages = [
    'Sincronizando pedidos con PocketBase…',
    'Organizando columnas por estado…',
    'Preparando repartidores, escoltas y pickers…',
    'Aplicando filtros de fecha y sucursal…',
    '«Un libro debe ser el hacha que rompa el mar helado que hay en nosotros.» — Kafka',
    'Casi listo. Tu tablero está por aparecer…',
  ];

  $: columns = DASHBOARD_CONFIG.displayStatuses;
  $: rows = Array(Math.ceil(columns.length / 3));

  let messageIndex = 0;
  let interval;

  onMount(() => {
    interval = setInterval(() => {
      messageIndex = (messageIndex + 1) % messages.length;
    }, 2800);
  });

  onDestroy(() => {
    clearInterval(interval);
  });
</script>

<div class="dashboard-loading px-2 sm:px-4">
  <div class="loading-header mb-8 text-center" in:fade={{ duration: 400 }}>
    <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-lg mb-4 logo-pulse">
      <img
        src="/assets/images/abindustrial_logo.png"
        alt="AB Industrial"
        class="h-10 w-10 object-contain"
      />
    </div>

    <h3 class="text-lg font-bold text-blueGray-800 mb-2">Estado de Pedidos</h3>

    <div class="h-1.5 w-full max-w-md mx-auto rounded-full bg-blueGray-200 overflow-hidden mb-4">
      <div class="loading-bar h-full rounded-full"></div>
    </div>

    {#key messageIndex}
      <p class="text-sm text-blueGray-600 font-medium min-h-[1.25rem]" in:fade={{ duration: 350 }}>
        {messages[messageIndex]}
      </p>
    {/key}
  </div>

  {#each rows as _, rowIndex}
    <div class="flex flex-wrap {rowIndex > 0 ? 'mt-4' : ''}">
      {#each columns.slice(rowIndex * 3, (rowIndex + 1) * 3) as status, colIndex}
        <div
          class="w-full xl:w-4/12 mb-12 xl:mb-0 px-4"
          in:fly={{ y: 24, duration: 500, delay: (rowIndex * 3 + colIndex) * 80 }}
        >
          <div
            class="rounded border shadow-lg overflow-hidden bg-gradient-to-b {statusTints[status] || statusTints.CANCELLED}"
            style="height: 480px;"
          >
            <div class="px-4 py-3 border-b border-white border-opacity-60">
              <div class="shimmer h-4 w-32 rounded"></div>
            </div>

            <div class="p-4 space-y-3">
              {#each [0, 1, 2] as cardIndex}
                <div
                  class="rounded-lg border border-white border-opacity-70 bg-white bg-opacity-60 p-3 space-y-2"
                  style="animation-delay: {(rowIndex * 3 + colIndex) * 120 + cardIndex * 90}ms"
                >
                  <div class="flex justify-between gap-2">
                    <div class="shimmer h-3 flex-1 rounded"></div>
                    <div class="shimmer h-3 w-10 rounded"></div>
                  </div>
                  <div class="shimmer h-2 w-24 rounded"></div>
                  <div class="shimmer h-2 w-full rounded"></div>
                  <div class="shimmer h-2 w-3/4 rounded"></div>
                </div>
              {/each}
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/each}
</div>

<style>
  .logo-pulse {
    animation: logoPulse 2s ease-in-out infinite;
  }

  .loading-bar {
    width: 40%;
    background: linear-gradient(90deg, #1c4f98, #3b82f6, #1c4f98);
    background-size: 200% 100%;
    animation: loadingBar 1.6s ease-in-out infinite;
  }

  .shimmer {
    background: linear-gradient(
      90deg,
      rgba(148, 163, 184, 0.25) 0%,
      rgba(148, 163, 184, 0.45) 50%,
      rgba(148, 163, 184, 0.25) 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.4s ease-in-out infinite;
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  @keyframes loadingBar {
    0% {
      transform: translateX(-120%);
      background-position: 0% 50%;
    }
    100% {
      transform: translateX(320%);
      background-position: 100% 50%;
    }
  }

  @keyframes logoPulse {
    0%,
    100% {
      transform: scale(1);
      box-shadow: 0 10px 25px rgba(28, 79, 152, 0.15);
    }
    50% {
      transform: scale(1.04);
      box-shadow: 0 14px 30px rgba(28, 79, 152, 0.25);
    }
  }
</style>
